import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { TestSetService } from "../../../../services/TestSetService";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { BeakerIcon } from "lucide-react";

interface ReviewBenchmarkTestsCardProps {
  benchmarkTestSetIds: number[];
}

const ReviewBenchmarkTestsCard: React.FC<ReviewBenchmarkTestsCardProps> = ({
  benchmarkTestSetIds,
}) => {
  const { apiKey } = usePermissions();
  const [testSetNames, setTestSetNames] = useState<string[]>([]);

  useEffect(() => {
    if (!benchmarkTestSetIds || benchmarkTestSetIds.length === 0) {
      setTestSetNames([]);
      return;
    }
    const names: string[] = benchmarkTestSetIds.map((id) => `Test Set #${id}`);
    let completed = 0;
    const subs: Array<{ unsubscribe: () => void }> = [];
    benchmarkTestSetIds.forEach((id, idx) => {
      const sub = TestSetService.getTestSet(
        id.toString(),
        () => {},
        apiKey
      ).subscribe(
        (ts: any) => {
          if (ts) names[idx] = ts.test_set_name || ts.name || `Test Set #${id}`;
          completed++;
          if (completed === benchmarkTestSetIds.length)
            setTestSetNames([...names]);
        },
        () => {
          completed++;
          if (completed === benchmarkTestSetIds.length)
            setTestSetNames([...names]);
        }
      );
      subs.push(sub);
    });
    return () => subs.forEach((s) => s.unsubscribe());
  }, [benchmarkTestSetIds?.join(","), apiKey]);

  if (!benchmarkTestSetIds || benchmarkTestSetIds.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
          <BeakerIcon className="w-5 h-5 text-maitai-green" />
          Benchmark Tests
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground font-gc-reg mb-3">
          {benchmarkTestSetIds.length} golden test set
          {benchmarkTestSetIds.length !== 1 ? "s" : ""} will run
          automatically after fine-tuning completes.
        </p>
        <div className="space-y-2">
          {testSetNames.length > 0
            ? testSetNames.map((name, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 border border-border">
                  <BeakerIcon className="w-4 h-4 text-maitai-green shrink-0" />
                  <span className="text-sm font-gc-med text-foreground">
                    {name}
                  </span>
                </div>
              ))
            : benchmarkTestSetIds.map((id) => (
                <Skeleton key={id} className="h-9 w-full" />
              ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewBenchmarkTestsCard;
